import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { X, Home, ChefHat, Bed, Bath, TreePalm, DoorOpen } from 'lucide-react';

// Lazy loading image component with placeholder
const LazyImage = ({ src, alt, title, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      className="relative overflow-hidden rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none cursor-pointer group aspect-square bg-muted"
      onClick={onClick}
    >
      {/* Placeholder skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
        </div>
      )}
      
      {isInView && (
        <img 
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
      
      <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/20 transition-all duration-300"></div>
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <h4 className="text-sm font-medium text-background truncate">
          {title}
        </h4>
      </div>
    </div>
  );
};

export const PropertyGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  const roomSections = [
    {
      id: 'living',
      title: 'Living & Dining',
      subtitle: 'Open-plan living with floor-to-ceiling windows',
      icon: Home,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/evbm1o7q_F%20%281%20of%2032%29.jpg',
          alt: 'Living room with floor-to-ceiling windows and patio access',
          title: 'Living Space'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/cecf0pum_F%20%282%20of%2032%29.jpg',
          alt: 'Open plan living and dining area',
          title: 'Open Plan Living'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/16nfcyq3_F%20%286%20of%2032%29.jpg',
          alt: 'Living area with triangular plant shelves',
          title: 'Living Details'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/vtzr00yg_F%20%287%20of%2032%29.jpg',
          alt: 'Living room corner with patio view',
          title: 'Natural Light'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/mays2lmi_F%20%288%20of%2032%29.jpg',
          alt: 'Dining and kitchen area view',
          title: 'Dining Space'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/v5pybrzw_F%20%289%20of%2032%29.jpg',
          alt: 'Dining area with artwork',
          title: 'Dining Corner'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/b2lzm9fe_F%20%2811%20of%2032%29.jpg',
          alt: 'Living space with floor-to-ceiling windows',
          title: 'Bright Living'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/gm7u29wv_F%20%2826%20of%2032%29.jpg',
          alt: 'Living room with patio view and dining area',
          title: 'Living & Dining View'
        }
      ]
    },
    {
      id: 'kitchen',
      title: 'Kitchen',
      subtitle: 'Modern German appliances with Scandinavian oak finishes',
      icon: ChefHat,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/b5q38nac_F%20%283%20of%2032%29.jpg',
          alt: 'Modern kitchen with Scandinavian oak',
          title: 'Modern Kitchen'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/efj6ekr8_F%20%284%20of%2032%29.jpg',
          alt: 'Kitchen and dining with natural light',
          title: 'Kitchen & Dining'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_3f7a9b47-efef-4444-aae5-aafe46cb5f39/artifacts/gjqnhmvn_F%20%285%20of%2032%29.jpg',
          alt: 'Evening ambiance in kitchen',
          title: 'Evening Light'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/vczbqepd_F%20%2810%20of%2032%29.jpg',
          alt: 'Open kitchen with oak cabinetry',
          title: 'Kitchen View'
        }
      ]
    },
    {
      id: 'bedrooms',
      title: 'Bedrooms',
      subtitle: 'Euro King bed + 2 convertible daybeds, sleeps 6',
      icon: Bed,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/ocj01xz4_F%20%2815%20of%2032%29.jpg',
          alt: 'Bedroom with custom oak headboard',
          title: 'Master Bedroom'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/h28frvjl_F%20%2818%20of%2032%29.jpg',
          alt: 'Master bedroom with floor-to-ceiling oak',
          title: 'Master Suite'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/jft58jnd_F%20%2820%20of%2032%29.jpg',
          alt: 'Bedroom with oak paneling and neon art',
          title: 'Bedroom View'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/949vgikr_F%20%2812%20of%2032%29.jpg',
          alt: 'Guest bedroom with twin beds and wooden fan',
          title: 'Guest Bedroom'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/jbyfncxd_F%20%2814%20of%2032%29.jpg',
          alt: 'Bedroom with work desk and art',
          title: 'Work Space'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/nkwte2kk_F%20%2816%20of%2032%29.jpg',
          alt: 'Oak headboard detail with HOME decor',
          title: 'Bedroom Detail'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/n92fo2il_F%20%2819%20of%2032%29.jpg',
          alt: 'Bedroom featuring oak wardrobe',
          title: 'Oak Wardrobe'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/fs20607p_F%20%2813%20of%2032%29.jpg',
          alt: 'Oak hallway with bathroom entrance',
          title: 'Hallway'
        }
      ]
    },
    {
      id: 'bathrooms',
      title: 'Bathrooms',
      subtitle: 'Sage green walls with terrazzo finishes',
      icon: Bath,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/kt3ty1qq_F%20%2821%20of%2032%29.jpg',
          alt: 'Bathroom with artwork and natural light',
          title: 'Main Bathroom'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/ymnp5zjr_F%20%2823%20of%2032%29.jpg',
          alt: 'Terrazzo shower with black fixtures',
          title: 'Terrazzo Shower'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/0s5m45n0_F%20%2824%20of%2032%29.jpg',
          alt: 'Second bathroom with sage walls and skylight',
          title: 'Second Bathroom'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/r86ciw9y_F%20%2825%20of%2032%29.jpg',
          alt: 'Bathroom vanity with terrazzo and circular mirror',
          title: 'Vanity Detail'
        }
      ]
    },
    {
      id: 'outdoor',
      title: 'Outdoor & Patio',
      subtitle: 'Private deck with lounge seating and city views',
      icon: TreePalm,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/ksiq3qh6_F%20%2827%20of%2032%29.jpg',
          alt: 'Patio with canvas awning',
          title: 'Covered Patio'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/1xznhp9w_F%20%2828%20of%2032%29.jpg',
          alt: 'Patio seating area with greenery',
          title: 'Outdoor Seating'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/1ng8b44g_F%20%2829%20of%2032%29.jpg',
          alt: 'Patio deck with urban views',
          title: 'Patio Deck'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/7s4gby6y_F%20%2830%20of%2032%29.jpg',
          alt: 'Patio lounge area with planters',
          title: 'Lounge Area'
        }
      ]
    },
    {
      id: 'entryway',
      title: 'Entryway',
      subtitle: 'Private entrance on the famous yellow streets of Cascais',
      icon: DoorOpen,
      images: [
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/7h8khywr_F%20%2831%20of%2032%29.jpg',
          alt: 'Exterior twilight view with warm interior lighting',
          title: 'Twilight View'
        },
        {
          url: 'https://customer-assets.emergentagent.com/job_cascais-luxury-apt/artifacts/ektcmiqr_F%20%2832%20of%2032%29.jpg',
          alt: 'Famous yellow streets of Cascais exterior',
          title: 'Yellow Streets'
        }
      ]
    }
  ];
  
  const totalPhotos = roomSections.reduce((sum, section) => sum + section.images.length, 0);
  
  return (
    <section className="py-16 sm:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl font-display font-semibold text-foreground mb-2">
            Photo Gallery
          </h2>
          <p className="text-base text-muted-foreground">
            {totalPhotos} photos • Click to enlarge
          </p>
        </div>
        
        {/* Room Sections */}
        <div className="space-y-16">
          {roomSections.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.id} id={section.id}>
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-tl-xl rounded-br-xl rounded-tr-none rounded-bl-none bg-primary/10">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-display font-semibold text-foreground">
                      {section.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {section.subtitle}
                    </p>
                  </div>
                  <div className="ml-auto">
                    <span className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
                      {section.images.length} photos
                    </span>
                  </div>
                </div>
                
                {/* Images Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {section.images.map((image, index) => (
                    <LazyImage
                      key={index}
                      src={image.url}
                      alt={image.alt}
                      title={image.title}
                      onClick={() => setSelectedImage(image)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/95 backdrop-blur-sm p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 p-2 rounded-full bg-background/10 hover:bg-background/20 text-background transition-smooth"
            onClick={() => setSelectedImage(null)}
          >
            <X size={24} />
          </button>
          <div className="max-w-6xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-tl-lg rounded-br-lg rounded-tr-none rounded-bl-none"
            />
          </div>
        </div>
      )}
    </section>
  );
};